import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Alert,
    Divider,
} from '@mui/material';
import { Save as SaveIcon, Public as PublicIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import websiteSettingsService from '../../../services/websiteSettingsService';
import storeService from '../../../services/storeService';
import ImageUploader from '../../common/ImageUploader';

// Editable storefront content. Keys are the contract with the storefront (chic-next-website/lib/content.ts):
// an empty value means "use the site's built-in default".
const EMPTY_CONTENT = {
    hero: { image: '', textTone: '', eyebrow: '', title1: '', title2: '', text: '' },
    maison: {
        image: '', eyebrow: '', quote1: '', quote2: '', body: '',
        stat1Value: '', stat1Label: '', stat2Value: '', stat2Label: '', stat3Value: '', stat3Label: '',
    },
    contact: { eyebrow: '', title: '', text: '', phone: '', email: '', address: '', hours: '' },
};

const mergeContent = (saved) => ({
    hero: { ...EMPTY_CONTENT.hero, ...(saved?.hero || {}) },
    maison: { ...EMPTY_CONTENT.maison, ...(saved?.maison || {}) },
    contact: { ...EMPTY_CONTENT.contact, ...(saved?.contact || {}) },
});

const WebsiteSettings = () => {
    const { t } = useTranslation();

    const [stores, setStores] = useState([]);
    const [formData, setFormData] = useState({
        shopStoreId: '',
        maintenanceMode: false,
        maintenanceMessage: '',
    });
    const [content, setContent] = useState(EMPTY_CONTENT);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setInitialLoading(true);
        try {
            const [settings, activeStores] = await Promise.all([
                websiteSettingsService.getSettings(),
                storeService.getActiveStores(),
            ]);
            setStores(activeStores || []);
            setFormData({
                shopStoreId: settings.shopStoreId || '',
                maintenanceMode: Boolean(settings.maintenanceMode),
                maintenanceMessage: settings.maintenanceMessage || '',
            });
            try {
                setContent(mergeContent(settings.content ? JSON.parse(settings.content) : {}));
            } catch {
                setContent(EMPTY_CONTENT);
            }
        } catch (err) {
            console.error('Failed to load website settings:', err);
            toast.error(t('common.loadError'));
        } finally {
            setInitialLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await websiteSettingsService.updateSettings({
                shopStoreId: formData.shopStoreId || null,
                maintenanceMode: formData.maintenanceMode,
                maintenanceMessage: formData.maintenanceMessage || null,
                content: JSON.stringify(content),
            });
            toast.success(t('websiteSettings.saveSuccess'));
        } catch (err) {
            toast.error(err.response?.data?.message || t('common.saveError'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const setField = (section, key) => (e) =>
        setContent((prev) => ({ ...prev, [section]: { ...prev[section], [key]: e.target.value } }));

    const textField = (section, key, label, props = {}) => (
        <TextField
            fullWidth
            size="small"
            label={label}
            value={content[section][key]}
            onChange={setField(section, key)}
            {...props}
        />
    );

    const imageField = (section, key, label) => (
        <ImageUploader
            label={label}
            currentUrl={content[section][key] || null}
            onUpload={async (file) => {
                const { url } = await websiteSettingsService.uploadImage(file);
                setContent((prev) => ({ ...prev, [section]: { ...prev[section], [key]: url } }));
                toast.success('Image téléversée — pensez à enregistrer.');
            }}
            onDelete={async () => {
                setContent((prev) => ({ ...prev, [section]: { ...prev[section], [key]: '' } }));
            }}
            size={200}
        />
    );

    if (initialLoading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>{t('common.loading')}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <PublicIcon color="primary" fontSize="large" />
                <Typography variant="h4">{t('websiteSettings.title')}</Typography>
            </Box>

            <form onSubmit={handleSubmit}>
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {t('websiteSettings.exposedStore')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('websiteSettings.exposedStoreHelper')}
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>{t('websiteSettings.selectStore')}</InputLabel>
                                    <Select
                                        value={formData.shopStoreId}
                                        label={t('websiteSettings.selectStore')}
                                        onChange={(e) =>
                                            setFormData({ ...formData, shopStoreId: e.target.value })
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>{t('websiteSettings.allStores')}</em>
                                        </MenuItem>
                                        {stores.map((s) => (
                                            <MenuItem key={s.id} value={s.id}>
                                                {s.name} ({s.code})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {t('websiteSettings.maintenanceMode')}
                        </Typography>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.maintenanceMode}
                                    onChange={(e) =>
                                        setFormData({ ...formData, maintenanceMode: e.target.checked })
                                    }
                                    color="warning"
                                />
                            }
                            label={t('websiteSettings.maintenanceEnabled')}
                        />

                        {formData.maintenanceMode && (
                            <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
                                {t('websiteSettings.maintenanceWarning')}
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            sx={{ mt: 2 }}
                            label={t('websiteSettings.maintenanceMessage')}
                            value={formData.maintenanceMessage}
                            onChange={(e) =>
                                setFormData({ ...formData, maintenanceMessage: e.target.value })
                            }
                            placeholder={t('websiteSettings.maintenanceMessagePlaceholder')}
                        />
                    </CardContent>
                </Card>

                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Contenu du site — page d&apos;accueil</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Modifiez les images et les textes du site. Un champ laissé vide utilise le texte ou l&apos;image par défaut.
                            Les produits de la section « Nouveautés » sont automatiquement les 10 derniers produits ajoutés qui sont en stock.
                        </Typography>

                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Bannière d&apos;accueil</Typography>
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={4}>{imageField('hero', 'image', 'Image de la bannière')}</Grid>
                            <Grid item xs={12} md={8}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>{textField('hero', 'eyebrow', 'Petit titre (ex : Depuis 1993)')}</Grid>
                                    <Grid item xs={12} sm={6}>{textField('hero', 'title1', 'Titre — ligne 1 (ex : Chic)')}</Grid>
                                    <Grid item xs={12} sm={6}>{textField('hero', 'title2', 'Titre — ligne 2 (ex : Parfumerie)')}</Grid>
                                    <Grid item xs={12}>{textField('hero', 'text', 'Texte', { multiline: true, rows: 3 })}</Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Couleur du texte sur l'image"
                                            value={content.hero.textTone === 'light' ? 'light' : 'dark'}
                                            onChange={setField('hero', 'textTone')}
                                            helperText="Choisissez « Texte clair » si votre image est sombre, sinon le texte n'est pas lisible."
                                        >
                                            <MenuItem value="dark">Texte foncé (pour une image claire)</MenuItem>
                                            <MenuItem value="light">Texte clair (pour une image sombre)</MenuItem>
                                        </TextField>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Section « À propos »</Typography>
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={4}>{imageField('maison', 'image', 'Image')}</Grid>
                            <Grid item xs={12} md={8}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>{textField('maison', 'eyebrow', 'Petit titre')}</Grid>
                                    <Grid item xs={12} sm={6}>{textField('maison', 'quote1', 'Citation — ligne 1')}</Grid>
                                    <Grid item xs={12} sm={6}>{textField('maison', 'quote2', 'Citation — ligne 2')}</Grid>
                                    <Grid item xs={12}>{textField('maison', 'body', 'Texte de présentation', { multiline: true, rows: 5 })}</Grid>
                                    {[1, 2, 3].map((n) => (
                                        <React.Fragment key={n}>
                                            <Grid item xs={5} sm={3}>{textField('maison', `stat${n}Value`, `Chiffre ${n}`)}</Grid>
                                            <Grid item xs={7} sm={9}>{textField('maison', `stat${n}Label`, `Légende ${n}`)}</Grid>
                                        </React.Fragment>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Section contact — commande sur mesure</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>{textField('contact', 'eyebrow', 'Petit titre')}</Grid>
                            <Grid item xs={12} sm={6}>{textField('contact', 'title', 'Titre')}</Grid>
                            <Grid item xs={12}>{textField('contact', 'text', 'Texte d\'introduction', { multiline: true, rows: 3 })}</Grid>
                            <Grid item xs={12} sm={4}>{textField('contact', 'phone', 'Téléphone')}</Grid>
                            <Grid item xs={12} sm={4}>{textField('contact', 'email', 'Email')}</Grid>
                            <Grid item xs={12} sm={4}>{textField('contact', 'hours', 'Horaires')}</Grid>
                            <Grid item xs={12}>{textField('contact', 'address', 'Adresse')}</Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={loading}>
                    {loading ? t('common.save') + '...' : t('common.save')}
                </Button>
            </form>
        </Box>
    );
};

export default WebsiteSettings;
